/**
 * @file RTNU Tabs Component
 * @description Reusable Web Component for accessible tab navigation
 *
 * Features:
 * - ARIA-compliant tab navigation
 * - Keyboard navigation (Arrow keys, Home, End)
 * - Auto-activates first tab on load
 *
 * Usage:
 * <rtnu-tabs>
 *   <div role="tablist" aria-label="Content Tabs">
 *     <button class="tab-button" data-tab-target="tab-1" role="tab">Tab 1</button>
 *     <button class="tab-button" data-tab-target="tab-2" role="tab">Tab 2</button>
 *   </div>
 *   <div id="tab-1" class="tab-panel" role="tabpanel">Content 1</div>
 *   <div id="tab-2" class="tab-panel" role="tabpanel">Content 2</div>
 * </rtnu-tabs>
 */

class RtnuTabs extends HTMLElement {
  constructor() {
    super();
    this.tabButtons = [];
    this.tabPanels = [];
    this.abortController = new AbortController();
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  /**
   * Initialize tabs component
   */
  init() {
    // Get all tab buttons and panels within this component
    this.tabButtons = Array.from(this.querySelectorAll('[role="tab"]'));
    this.tabPanels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (this.tabButtons.length === 0) {
      console.warn('RtnuTabs: No tab buttons found');
      return;
    }

    // Setup event listeners
    this.setupEventListeners();

    // Activate first tab by default if none are active
    const hasActiveTab = this.tabButtons.some(btn =>
      btn.classList.contains('active') || btn.getAttribute('aria-selected') === 'true'
    );

    if (!hasActiveTab && this.tabButtons.length > 0) {
      this.activateTab(this.tabButtons[0]);
    }
  }

  /**
   * Setup event listeners for tabs
   */
  setupEventListeners() {
    const { signal } = this.abortController;

    this.tabButtons.forEach((button, index) => {
      // Click handler
      button.addEventListener('click', () => {
        this.activateTab(button);
      }, { signal });

      // Keyboard navigation
      button.addEventListener('keydown', (e) => {
        this.handleKeydown(e, index);
      }, { signal });
    });
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} e - Keyboard event
   * @param {number} currentIndex - Current tab button index
   */
  handleKeydown(e, currentIndex) {
    let targetButton = null;

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        targetButton = this.tabButtons[currentIndex + 1] || this.tabButtons[0];
        break;
      case 'ArrowLeft':
        e.preventDefault();
        targetButton = this.tabButtons[currentIndex - 1] || this.tabButtons[this.tabButtons.length - 1];
        break;
      case 'Home':
        e.preventDefault();
        targetButton = this.tabButtons[0];
        break;
      case 'End':
        e.preventDefault();
        targetButton = this.tabButtons[this.tabButtons.length - 1];
        break;
    }

    if (targetButton) {
      this.activateTab(targetButton);
    }
  }

  /**
   * Activate a specific tab
   * @param {HTMLElement} button - Tab button to activate
   */
  activateTab(button) {
    const targetId = button.dataset.tabTarget;

    if (!targetId) {
      console.warn('RtnuTabs: Tab button missing data-tab-target attribute');
      return;
    }

    // Deactivate all tabs
    this.tabButtons.forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
      btn.setAttribute('tabindex', '-1');
    });

    this.tabPanels.forEach(panel => {
      panel.classList.remove('active');
      panel.setAttribute('hidden', '');
    });

    // Activate selected tab
    button.classList.add('active');
    button.setAttribute('aria-selected', 'true');
    button.setAttribute('tabindex', '0');
    button.focus();

    // Show corresponding panel
    const targetPanel = this.querySelector(`#${targetId}`);
    if (targetPanel) {
      targetPanel.classList.add('active');
      targetPanel.removeAttribute('hidden');
    } else {
      console.warn(`RtnuTabs: Panel with id "${targetId}" not found`);
    }
  }
}

if (!customElements.get('rtnu-tabs')) {
  customElements.define('rtnu-tabs', RtnuTabs);
}

export default RtnuTabs;
